import moment from 'moment';
import store from '@/store';
import { notifyPresets } from '@/components/agents-table/services/notifyPresets';
import { dialogPresets } from '@/services/dialogPresets';
import COLORS from '@/design-system/colors.json';

export const agentsUtil = {
  data() {
    return {
      agentRowStylesConfig: {
        isIncompatible: '#fff0ee',
        selected: {
          defaultBG: '#E0F9F3',
          isIncompatible: '#FFE1DD',
        },
        initial: '#FFFFFF',
      },
    };
  },
  mixins: [notifyPresets, dialogPresets],
  methods: {
    getAgentRowStyle(agent, selected) {
      const bgColorsMap = {
        initial: '#FFFFFF',
        updating: '#fff0ee',
        selected: '#E0F9F3',
        incompatible: '#FFE1DD',
      };

      const isDuplicateLicense = this.isAgentDuplicateLicense(agent);
      const isIncompatible = this.isIncompatibleVersion(agent);
      const isAgentCloud = this.isAgentIsCloud(agent);
      const isAgentInProgress = this.isAgentInProgress(agent);
      const agentRowStyle = {
        background: bgColorsMap.initial,
        color: isDuplicateLicense ? COLORS['Text/text_low_old'] : COLORS['Text/text_medium'],
      };
      if (selected) {
        if (!isAgentCloud && (isIncompatible || isDuplicateLicense)) {
          agentRowStyle.background = bgColorsMap.incompatible;
        } else agentRowStyle.background = bgColorsMap.selected;
      } else if ((!isAgentCloud && isIncompatible && !isDuplicateLicense) || isAgentInProgress) {
        agentRowStyle.background = bgColorsMap.updating;
      }

      return agentRowStyle;
    },
    shouldShowUpdateAgentButton(agent) {
      return this.isIncompatibleVersion(agent) && !this.isAgentStatusUpdating(agent) && !this.isAgentOffline(agent) && !this.isAgentIsCloud(agent) && !this.isAgentDuplicateLicense(agent);
    },
    isAgentBuildAvoidance(agent) {
      return agent.License && !(!agent.License.BuildAvoidance || agent.License.BuildAvoidance === 'false');
    },
    isAgentLocal(agent) {
      return agent.License && !agent.License.RegisteredInitiator && agent.License.RegisteredCores === 0;
    },
    isAgentIsCloud(agent = {}) {
      const clouds = ['Azure', 'AWS', 'GoogleCloud'];
      return clouds.includes(agent.Type);
    },
    isAgentHelper(agent = {}) {
      if (this.isAgentIsCloud(agent)) return true;
      return agent.License && agent.License.HelperType && agent.License.HelperType !== 'None';
    },
    isAgentInitiator(agent = {}) {
      return agent.License && agent.License.InitiatorType && agent.License.InitiatorType !== 'None' && agent.License.CIInitiatorType === 'None';
    },
    isAgentCIInitiator(agent = {}) {
      return agent.License && agent.License.CIInitiatorType && agent.License.CIInitiatorType !== 'None' && agent.License.InitiatorType === 'None';
    },
    isAgentDuplicateLicense(agent = {}) {
      return agent.License && agent.License.Status === 3;
    },
    isAgentLastDuplicateLicense(agent = {}) {
      return agent.License && agent.License.Status === 3 && agent.lastDuplicate === 1;
    },
    isIncompatibleVersion(agent = {}) {
      const ver = store.getters['settings/getSettings']?.CoordinatorVersion?.buildVersion;
      return ver !== agent.AgentVersion?.buildVersion;
    },
    isLowerThanTenVersion(agent = {}) {
      const majorVersionNum = agent.AgentVersion?.majorVersion;
      return majorVersionNum < 10;
    },
    isAgentOffline(agent = {}) {
      return agent.Status === 'Offline';
    },
    isAgentStatusUpdating(agent = {}) {
      return agent.Status === 'Updating';
    },
    isAgentOnline(agent = {}) {
      // what is online agent defined by Liron in WIN-4067
      return !this.isAgentOffline(agent) && !this.isIncompatibleVersion(agent) && !this.isAgentIsCloud(agent) && !this.isAgentDuplicateLicense(agent);
    },
    isAgentHelperDisabled(agent) {
      return !agent.Enabled || agent.Enabled === 'false';
    },
    isAgentHelperPending(agent) {
      return agent.License && agent.License.HelperType && agent.License.HelperType !== 'None' && agent.License.QueuedCores > 0 && agent.License.RegisteredCores === 0;
    },
    isAgentInitiatorPending(agent) {
      return agent.License && agent.License.InitiatorType && agent.License.InitiatorType !== 'None' && agent.License.CIInitiatorType === 'None' && agent.License.InitiatorQueued === true;
    },
    isAgentCIInitiatorPending(agent) {
      return agent.License && agent.License.CIInitiatorType && agent.License.CIInitiatorType !== 'None' && agent.License.InitiatorType === 'None' && agent.License.InitiatorQueued === true;
    },
    LastConnectedDate(agent) {
      return moment.unix(agent.LastConnected / 1000).format('DD/MM/YYYY hh:mm A');
    },
    isAgentInProgress(agent) {
      return agent.Status === 'Installing' || agent.Status === 'Downloading' || agent.Status === 'Updating';
    },
    arrayOfObjectsToLowerKeys(originArr) {
      return originArr.map(item =>
        Object.keys(item).reduce((accumulator, key) => {
          accumulator[key.toLowerCase()] = item[key];
          return accumulator;
        }, {}),
      );
    },
    handleActionDialog(results, isBulk) {
      const formattedResults = this.arrayOfObjectsToLowerKeys(results);
      const updatedAgents = formattedResults.filter(result => result.status === 'OK');
      if (updatedAgents.length === results.length) {
        this.notifyPresets.agentsActionSuccess();
        return;
      }
      if (isBulk) {
        this.dialogPresets.agentsBulkActionPartialSuccessOrFail(results, updatedAgents.length);
      } else {
        this.notifyPresets.agentSingeActionFail();
      }
    },
    updateAgentsVersion(agents) {
      const API_CALLS = [];
      agents.forEach(agent => API_CALLS.push(store.dispatch('agents/updateAgentsVersion', agent.Name)));
      return Promise.all(API_CALLS)
        .then(results => {
          const updatedAgents = results.filter(agent => agent.Status === 'true');
          if (updatedAgents.length !== results.length) {
            this.dialogPresets.agentsBulkActionPartialSuccessOrFail(results, updatedAgents.length);
          }
        })
        .catch(error => {
          console.error('updateAgentsVersion error', error.message);
        });
    },
  },
};

export class Agent {
  constructor(agent) {
    Object.keys(agent).forEach(key => {
      this[key] = agent[key];
    });
  }

  // Getter
  get ibxMeta() {
    return {
      isInProgress: agentsUtil.methods.isAgentInProgress(this),
      isLocal: agentsUtil.methods.isAgentLocal(this),
      isInitiatorPending: agentsUtil.methods.isAgentInitiatorPending(this),
      isCIInitiatorPending: agentsUtil.methods.isAgentCIInitiatorPending(this),
      isHelperPending: agentsUtil.methods.isAgentHelperPending(this),
      isHelperDisabled: agentsUtil.methods.isAgentHelperDisabled(this),
      isBuildAvoidance: agentsUtil.methods.isAgentBuildAvoidance(this),
      isIncompatibleVersion: agentsUtil.methods.isIncompatibleVersion(this),
      isAgentDuplicateLicense: agentsUtil.methods.isAgentDuplicateLicense(this),
      isAgentLastDuplicateLicense: agentsUtil.methods.isAgentLastDuplicateLicense(this),
      shouldShowUpdateAgentButton: agentsUtil.methods.shouldShowUpdateAgentButton(this),
      getAgentRowStyle: agentsUtil.methods.getAgentRowStyle(this),
      isAgentIsCloud: agentsUtil.methods.isAgentIsCloud(this),
      isAgentHelper: agentsUtil.methods.isAgentHelper(this),
      isAgentInitiator: agentsUtil.methods.isAgentInitiator(this),
      isAgentCIInitiator: agentsUtil.methods.isAgentCIInitiator(this),
      isAgentOffline: agentsUtil.methods.isAgentOffline(this),
      isAgentOnline: agentsUtil.methods.isAgentOnline(this),
      isLowerThanTenVersion: agentsUtil.methods.isLowerThanTenVersion(this),
      LastConnectedDate: agentsUtil.methods.LastConnectedDate(this),
      update: () => agentsUtil.methods.updateAgentsVersion([this]),
    };
  }
}
